import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getWorkOrderDetail, addWorkOrder, updateWorkOrder } from "../../api/workorders";
import { getAllFinishedGoods } from "../../api/finishedGoods";
import dayjs from "dayjs";

interface WorkOrderFormData {
  code: string;
  finishedGoodId: string;
  estimateCompletionDate: Date | null;
}

interface FinishedGood {
  id: string;
  name: string;
}

interface WorkOrderFormProps {
  id?: string;
  companyId?: string;
  onSubmit: () => void;
}

const validationSchema = Yup.object({
  code: Yup.string(),
  finishedGoodId: Yup.string()
    .required("El producto terminado es requerido"),
  estimateCompletionDate: Yup.date()
    .nullable()
    .min(new Date(), "La fecha estimada no puede ser anterior a hoy")
});

export default function WorkOrderForm({ id, companyId, onSubmit }: WorkOrderFormProps) {
  const [finishedGoods, setFinishedGoods] = useState<FinishedGood[]>([]);
  const { openSnack } = useSnack();

  const initialValues: WorkOrderFormData = {
    code: "",
    finishedGoodId: "",
    estimateCompletionDate: null,
  };

  useEffect(() => {
    fetchFinishedGoods();
  }, []);

  const fetchWorkOrder = async (setFieldValue: (field: string, value: any) => void) => {
    if (!id) return;
    
    try {
      const response = await getWorkOrderDetail(id);
      const data = response.data;
      setFieldValue("code", data.code);
      setFieldValue("finishedGoodId", data.finishedGoodId);
      setFieldValue("estimateCompletionDate", data.estimateCompletionDate);
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const fetchFinishedGoods = async () => {
    try {
      const response = await getAllFinishedGoods();
      setFinishedGoods(response.data);
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const handleSubmit = async (values: WorkOrderFormData, { setSubmitting }: any) => {
    const payload = {
      ...values,
      companyId: companyId,
      estimateCompletionDate: dayjs(values.estimateCompletionDate).format("YYYY-MM-DD"),
    }
    try {
      if (id) {
        await updateWorkOrder(id, payload);
        openSnack("Orden de trabajo actualizada exitosamente", "success");
      } else {
        await addWorkOrder(payload);
        openSnack("Orden de trabajo creada exitosamente", "success");
      }
      onSubmit();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, isSubmitting, setFieldTouched }) => (
        <Form>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field
                  name="finishedGoodId"
                  as={DropdownSelector}
                  required
                  fullWidth
                  fetchFunction={getAllFinishedGoods}
                  optionLabel={["code", "name"]}
                  optionSeparator=" | "
                  keyField="id"
                  label="Producto Terminado"
                  error={touched.finishedGoodId && Boolean(errors.finishedGoodId)}
                  options={finishedGoods.map((fg) => ({
                    value: fg.id,
                    label: fg.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12}>
                <MyDatePicker
                  label="Fecha Estimada de Terminación"
                  value={values.estimateCompletionDate}
                  onChange={(date) => {
                    setFieldValue("estimateCompletionDate", date);
                    setFieldTouched("estimateCompletionDate", true);
                  }}
                  error={touched.estimateCompletionDate && Boolean(errors.estimateCompletionDate)}
                  helperText={touched.estimateCompletionDate && errors.estimateCompletionDate}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={() => {
                    console.log(values);
                    console.log(errors);
                  }}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSubmitting ? "Procesando..." : (id ? "Actualizar" : "Crear")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
} 